import type { ResumeData, ResumeSettings } from '../types/resume';
import { Mail, Phone, MapPin, Globe, Linkedin, ExternalLink } from 'lucide-react';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function PortfolioTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header
        className="text-white px-10 py-10 -mx-10 -mt-10 mb-8"
        style={{ textAlign: s.headerAlignment, background: 'linear-gradient(to bottom right, #4f46e5, #9333ea, #ec4899)' }}
      >
        {s.photo && <img src={s.photo} alt="" className="w-24 h-24 rounded-full object-cover mx-auto mb-3 border-2 border-white/80" />}
        <h1 className="font-bold tracking-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${2.5 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>
        <div className="flex flex-wrap gap-5 mt-5 text-sm justify-center" style={{ color: 'rgba(255,255,255,0.9)' }}>
          {personal.email && <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" /> {personal.email}</span>}
          {personal.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" /> {personal.phone}</span>}
          {personal.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {personal.location}</span>}
          {personal.website && <span className="flex items-center gap-1.5"><Globe className="h-4 w-4" /> {personal.website}</span>}
          {personal.linkedin && <span className="flex items-center gap-1.5"><Linkedin className="h-4 w-4" /> {personal.linkedin}</span>}
        </div>
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{ fontSize: `${1.125 * hScale}rem` }}>
              <span className="w-8 h-1 bg-indigo-500 rounded-full"></span>WORK EXPERIENCE
            </h2>
            <div className="grid gap-4">
              {experience.map((exp) => (
                <div key={exp.id} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-slate-900" style={{ fontSize: `${1.125 * hScale}rem` }}>{exp.title}</h3>
                      <p className="text-indigo-600 font-semibold mt-0.5" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.company}{exp.location && ` · ${exp.location}`}</p>
                    </div>
                    <span className="font-medium text-slate-500 bg-white px-3 py-1 rounded-full border border-slate-200" style={{ fontSize: `${0.75 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </div>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{ fontSize: `${1.125 * hScale}rem` }}>
              <span className="w-8 h-1 bg-purple-500 rounded-full"></span>Featured Projects
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {projects.map((proj) => (
                <div key={proj.id} className="rounded-xl p-5 border border-slate-100" style={{ background: 'linear-gradient(to bottom right, #f8fafc, white)' }}>
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{proj.name}</h3>
                    {proj.url && <a href={proj.url} className="text-indigo-500 hover:text-indigo-600"><ExternalLink className="h-4 w-4" /></a>}
                  </div>
                  <p className="text-slate-600 mt-2" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {proj.technologies.map((t) => <span key={t} className="text-xs font-medium bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md" style={{ fontSize: `${0.75 * bScale}rem` }}>{t}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{ fontSize: `${1.125 * hScale}rem` }}>
              <span className="w-8 h-1 bg-pink-500 rounded-full"></span>Skills
            </h2>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <span key={skill.id} className="inline-flex items-center rounded-md bg-slate-700 px-3 py-1.5 text-xs font-medium text-white" style={{ fontSize: `${0.75 * bScale}rem` }}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{ fontSize: `${1.125 * hScale}rem` }}>
              <span className="w-8 h-1 bg-indigo-500 rounded-full"></span>Education
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <div key={edu.id} className="flex justify-between items-baseline pb-3 border-b border-slate-100">
                  <div>
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{edu.school}</h3>
                    <p className="text-slate-600" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                  </div>
                  <span className="text-slate-500" style={{ fontSize: `${0.875 * bScale}rem` }}>
                    {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{ fontSize: `${1.125 * hScale}rem` }}>
              <span className="w-8 h-1 bg-purple-500 rounded-full"></span>Certifications
            </h2>
            <div className="flex flex-wrap gap-3">
              {certifications.map((cert) => (
                <div key={cert.id} className="bg-slate-900 text-white px-4 py-2 rounded-lg">
                  <span className="font-semibold" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.name}</span>
                  <span className="text-slate-400 ml-2" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
